import amImage from '@assets/channelImages/amazon.png';
import fenicioImage from '@assets/channelImages/fenicio.png';
import mlImage from '@assets/channelImages/ml.png';
import retailImage from '@assets/channelImages/retail.png';
import shopifyImage from '@assets/channelImages/shopify.png';
import testChannelImage from '@assets/channelImages/test.svg';
import tnImage from '@assets/channelImages/tn.png';
import wooImage from '@assets/channelImages/woo.png';
import { ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationConnectorReadmodel, SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import useAuth from '@contexts/useAuth/useAuth';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

export type IntegrationsContextType = {
  connectors: Record<string, SystemIntegrationConnectorReadmodel>;
  integrations: Record<string, SystemIntegrationsReadmodel>;
  salesChannels: Record<string, SystemIntegrationsReadmodel>;
  erps: Record<string, SystemIntegrationsReadmodel>;
  isLoading: boolean;
  error: Error | null;
};

const integrationsContext = createContext<IntegrationsContextType | undefined>( undefined );

export const ProvideIntegrations = ({ children }: { children: ReactNode }) => {
  const { backend } = useBackend() as BackendContextType;
  const auth = useAuth();
  const userId = auth?.user?.id ?? null;
  const [ connectors, setConnectors ] = useState<Record<string, SystemIntegrationConnectorReadmodel>>({});
  const connectorsRef = useRef<Record<string, SystemIntegrationConnectorReadmodel>>({});
  const [ integrations, setIntegrations ] = useState<Record<string, SystemIntegrationsReadmodel>>({});
  const [ salesChannels, setSalesChannels ] = useState<Record<string, SystemIntegrationsReadmodel>>({});
  const [ erps, setErps ] = useState<Record<string, SystemIntegrationsReadmodel>>({});
  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ error, setError ] = useState<Error | null>( null );

  // const refresh = useCallback( async () => {
  useEffect(() => {
    if ( !backend || !userId ) {
      return;
    }
    setIsLoading( true );
    setError( null );
    try {
      const imagesMap = {
        mercadolibre: mlImage,
        tiendanube: tnImage,
        amazon: amImage,
        shopify: shopifyImage,
        fenicio: fenicioImage,
        woocommerce: wooImage,
        retailstore: retailImage,
        testchannel: testChannelImage
      };
      backend.queries.SystemsIntegrationsConnectors({}).then(( connectors ) => {
        const connectorsMap = connectors.reduce<Record<string, SystemIntegrationConnectorReadmodel>>(( acc, connector ) => {
          acc[connector.id] = connector as SystemIntegrationConnectorReadmodel;
          acc[connector.id].imageUrl = imagesMap[connector.id] || acc[connector.id].imageUrl; 
          return acc;
        }, {} as Record<string, SystemIntegrationConnectorReadmodel> );
        connectorsRef.current = connectorsMap;
        setConnectors( connectorsMap );
      });

      const subscription = backend.subscriptions.ExternalSystemsIntegrations({}, ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
        // Only maintain the raw `integrations` map here. The sales-channel / ERP
        // split is DERIVED reactively below (from integrations + connectors), so
        // it stays correct even when the connectors query resolves AFTER this
        // snapshot — see the classification effect.
        if ( change.type === 'snapshot' ) {
          const allIntegrations: Record<string, SystemIntegrationsReadmodel> = {};
          for ( const item of change.data.items ) {
            allIntegrations[item.id] = item;
          }
          setIntegrations( allIntegrations );
          return;
        }
        if ( change.type !== 'insert' && change.type !== 'update' ) return;
        const integration = change.data;
        setIntegrations(( existingIntegrations ) => ({
          ...existingIntegrations,
          [integration.id]: integration
        }));
      });

      return () => subscription.close();
    } catch ( err ) {
      setError( err as Error );
      setConnectors({});
      setIntegrations({});
      setSalesChannels({});
      setErps({});
    } finally {
      setIsLoading( false );
    }
  }, [ backend, userId ]);

  // Derive the sales-channel vs ERP split from (integrations + connector
  // catalog). Reactive on BOTH so the classification is correct regardless of
  // which resolves first — previously this was decided once, at the integrations
  // snapshot, using the connector ref; if the connectors query hadn't resolved
  // yet, every channel fell through to `erps` and `salesChannels` stayed empty
  // for the whole session (e.g. no channels in the publication create step).
  useEffect(() => {
    const channels: Record<string, SystemIntegrationsReadmodel> = {};
    const erpSystems: Record<string, SystemIntegrationsReadmodel> = {};
    for ( const item of Object.values( integrations )) {
      if ( connectors[item.connectorId]?.type === 'salesChannel' ) {
        channels[item.id] = item;
      } else {
        erpSystems[item.id] = item;
      }
    }
    setSalesChannels( channels );
    setErps( erpSystems );
  }, [ integrations, connectors ]);


  return (
    <integrationsContext.Provider value={{
      connectors,
      integrations,
      salesChannels,
      erps,
      isLoading,
      error
    }}>
      {children}
    </integrationsContext.Provider>
  );
};

export const useIntegrations = () => useContext( integrationsContext );
